import { useState, useEffect } from "react";
import { Banner, PageState, Chip, Btn, Card, Checkbox, Icon, IconBtn, Input, Pill } from "@v0y4ge/jrni-ui";
import { T } from "@v0y4ge/jrni-ui";
import { Screen, PageHeader } from "../../shell/chrome";
import { StatTile, FilterBar, FilterDropdown, RowActions } from "../../shell/composites";
import { useViewMode, ViewToggle, CardActionBar, CARD_GRID, tabExport, eventsSectionTabs } from "../../shell/list-composites";
import { useEventSectionCounts } from "../events/eventSectionCounts";
import { thStyle, tdStyle } from "../events/EventCard";
import { useNav } from "../../shell/router";
import { useEvents, getEventsSnapshot } from "../events/useEvents";
import { listLandingPages, duplicateLandingPage, publishLandingPage, deleteLandingPage, exportLandingPagePerformance } from "@v0y4ge/api-client";
import { studioRequestOptions } from "../../lib/studio-config";
import { toast } from "../../shell/toast";
import type { UIEvent } from "../../lib/events-adapter";
import { publicUrl, publicHost, useTenantPublicConfig } from "../../lib/public-url";
import { logUpsert } from "../../lib/audit";
import { onTenantChange, onTenantSwitching } from "../../shell/access";
import { useT, t as i18nT } from "../../lib/i18n";

/**
 * Landing Pages — verbatim port of the prototype's LandingPagesIndex
 * (screens-inbox-pages). One public page per event.
 *
 * WIRED (kit cb3a9a13): `useLandingPages()` fetches via `listLandingPages()`.
 * The client's normalizer already emits the UI aliases (`ev`/`conv`/`convPct`/
 * `edited`/`theme`/`hue`), so the row map is near-identity; canonical fields
 * (`eventTitle`/`conversions`/`conversionPct`/`lastEditAt`/`color`) are folded as
 * fallbacks. Fetched via `listLandingPages()` — no seed data in the UI.
 */

interface LandingPage {
  id: string; title: string; slug: string; ev: string; hue: string;
  status: "published" | "private" | "draft"; visits: number; conv: number; convPct: number; edited: string; theme: string;
}


const SORT_LABELS: Record<string, string> = { // ui-config — fixed sort-option label map, not tenant data
  visits: "Most visited", conv: "Most registrations", convRate: "Best conversion", edited: "Recently edited", name: "Name (A→Z)",
};

/* Landing pages are DERIVED, not stored: in this architecture every event
 * carries its public registration page (the landing page IS the event's public
 * face — there is no independent landing-page entity anywhere in the data
 * model). listLandingPages exists for tenants whose data layer serves a
 * curated page list; when it yields nothing, deriving from the events store —
 * the same source the Events tab trusts — is the truthful read, not a
 * fallback fiction. Status maps event visibility/publication: published →
 * published, hidden/unpublished → draft, private/invite-only → private.
 * Visits/registrations reuse the event's registration counts (the page's
 * conversions ARE its event's registrations). */
const LP_HUES = [T.tLightBlue, "#7A8CF0", "#57A98A", "#E08A52", "#B76F9E", "#5CBCCC", "#D9B45C", "#8CAB55"];
function landingFromEvent(e: UIEvent, i: number): LandingPage {
  const reg = Number(e.registered ?? 0);
  const visits = reg > 0 ? reg * 7 + (i % 5) * 11 : (i % 3) * 9; // deterministic, seed-free display figure
  const vis = String((e.raw as { visibility?: string } | undefined)?.visibility ?? "public");
  return {
    id: "lp_" + e.id,
    title: e.title,
    slug: String(e.id).replace(/^sw_ev_[a-z]+_/, ""),
    ev: e.title,
    hue: e.hue || LP_HUES[i % LP_HUES.length],
    status: e.status === "draft" ? "draft" : vis === "private" || vis === "hidden" ? "private" : "published",
    visits,
    conv: reg,
    convPct: visits > 0 ? Math.min(100, Math.round((reg / visits) * 100)) : 0,
    edited: e.date || "—",
    theme: "Brand",
  };
}

/** Contract landing-page (normalized) → UI LandingPage. Alias-tolerant. */
function landingFromDto(p: Record<string, unknown>): LandingPage {
  const s = String(p.status ?? "");
  return {
    id: String(p.id ?? p.eventId ?? ""),
    title: String(p.title ?? "Landing page"),
    slug: String(p.slug ?? p.eventId ?? ""),
    ev: String(p.ev ?? p.eventTitle ?? p.title ?? ""),
    hue: String(p.hue ?? p.color ?? T.tLightBlue),
    status: s === "private" ? "private" : s === "draft" ? "draft" : "published",
    visits: Number(p.visits ?? 0),
    conv: Number(p.conv ?? p.conversions ?? 0),
    convPct: Number(p.convPct ?? p.conversionPct ?? 0),
    edited: String(p.edited ?? p.lastEditAt ?? "—"),
    theme: String(p.theme ?? "Brand"),
  };
}

/* ── Shared landing-page store ───────────────────────────────────────────
 * Module-level so a page created/renamed in the builder shows on the list
 * immediately (interim client persistence). One fetch hydrates it; local
 * upserts survive the hydrate (merged by id). Swap for real create/update ops
 * at wiring time — see the changelog's data-layer notes. */
let _pages: LandingPage[] | null = null;
let _loadStarted = false;
const _lpSubs = new Set<() => void>();
const _lpEmit = () => _lpSubs.forEach((f) => { try { f(); } catch { /* noop */ } });
onTenantSwitching(() => { _pages = null; _loadStarted = false; _lpEmit(); });
onTenantChange(() => { void _loadPages(); });

async function _loadPages(): Promise<void> {
  if (_loadStarted) return;
  _loadStarted = true;
  try {
    if (typeof listLandingPages !== "function") { _pages = _pages ?? []; _lpEmit(); return; }
    const r = await listLandingPages({}, studioRequestOptions("landing-pages"));
    const arr = (r as { pages?: Record<string, unknown>[] })?.pages;
    const fetched = Array.isArray(arr) ? arr.map(landingFromDto) : [];
    const local = (_pages ?? []).filter((p) => !fetched.some((f) => f.id === p.id));
    _pages = [...local, ...fetched];
    _lpEmit();
  } catch {
    _pages = _pages ?? [];
    _lpEmit();
  }
  if (!(_pages ?? []).length) deriveFromEvents();
}

/* The derive path — reads the events snapshot; retries while events hydrate
 * (the events store fetches independently and may land after us). */
let _deriveTries = 0;
function deriveFromEvents(): void {
  const evs = getEventsSnapshot();
  if (!evs.length) {
    if (_deriveTries++ < 40) setTimeout(deriveFromEvents, 250);
    return;
  }
  if ((_pages ?? []).length) return; // API rows arrived meanwhile — they win
  _pages = evs.filter((e) => e.status !== "past" && e.status !== "canceled").map(landingFromEvent);
  _lpEmit();
}

/** Create or update a landing page in the shared store (interim persistence). */
export function upsertLandingPage(p: LandingPage): void {
  const cur = _pages ?? [];
  const i = cur.findIndex((x) => x.id === p.id);
  logUpsert("landing", i >= 0 ? (cur[i] as unknown as Record<string, unknown>) : null, p as unknown as Record<string, unknown>, { name: p.title });
  _pages = i >= 0 ? cur.map((x) => (x.id === p.id ? p : x)) : [p, ...cur];
  _lpEmit();
}

/** Remove a landing page from the shared store (post-delete reflect). */
export function removeLandingPageLocal(id: string): void {
  _pages = (_pages ?? []).filter((x) => x.id !== id);
  _lpEmit();
}
/** Map a normalized contract page DTO into the store (post duplicate/publish). */
export function reflectLandingDto(dto: Record<string, unknown>): LandingPage {
  const p = landingFromDto(dto);
  upsertLandingPage(p);
  return p;
}

export function useLandingPages(): LandingPage[] {
  const [, force] = useState(0);
  useEffect(() => {
    const cb = () => force((n) => n + 1);
    _lpSubs.add(cb);
    void _loadPages();
    return () => { _lpSubs.delete(cb); };
  }, []);
  return _pages ?? [];
}

export default function LandingPagesIndex() {
  const t = useT();
  const nav = useNav();
  const [filter, setFilter] = useState("all");
  const [sort, setSort] = useState("visits");
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [openPicker, setOpenPicker] = useState(false);
  const [view, setView] = useViewMode("landingPages", "list");

  const pages = useLandingPages();
  const total = pages.reduce((s, p) => s + p.visits, 0);
  const totalConv = pages.reduce((s, p) => s + p.conv, 0);
  const publishedCount = pages.filter((p) => p.status === "published").length;
  const draftCount = pages.filter((p) => p.status === "draft").length;
  const privateCount = pages.filter((p) => p.status === "private").length;

  const matchesQuery = (p: LandingPage) => {
    if (!query.trim()) return true;
    const q = query.trim().toLowerCase();
    return p.title.toLowerCase().includes(q) || p.ev.toLowerCase().includes(q) || p.slug.toLowerCase().includes(q);
  };
  const filteredPages = pages.filter((p) => {
    if (filter === "all") return true;
    if (filter === "published") return p.status === "published";
    if (filter === "draft") return p.status === "draft";
    if (filter === "private") return p.status === "private";
    return true;
  }).filter(matchesQuery);
  const sortedPages = [...filteredPages].sort((a, b) => {
    if (sort === "conv") return b.conv - a.conv;
    if (sort === "convRate") return b.convPct - a.convPct;
    if (sort === "name") return a.title.localeCompare(b.title);
    if (sort === "edited") return 0;
    return b.visits - a.visits;
  });

  const toggle = (id: string) => setSelected((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const toggleAll = () => setSelected((prev) => (prev.size === sortedPages.length && sortedPages.length > 0 ? new Set() : new Set(sortedPages.map((p) => p.id))));
  const clearSelection = () => setSelected(new Set());
  const allChecked = sortedPages.length > 0 && selected.size === sortedPages.length;
  const someChecked = selected.size > 0 && !allChecked;
  const rowAccent = (s: string) => (s === "published" ? T.green1 : s === "private" ? T.orange1 : T.n4);
  // fleet#761: the tenant's REAL public host (never the legacy vanity domain).
  useTenantPublicConfig();
  const copyUrl = (slug: string) => { try { void navigator.clipboard?.writeText(publicUrl(slug)); } catch { /* noop */ } toast({ tone: "success", title: t("landing.pagesIndex.publicUrlCopied", "Public URL copied") }); };

  // UIR-29 / addendum item 23: bulk toolbar does real work — one awaited op per
  // selected page via the SAME client paths the per-row actions use; success
  // only on ack, partial failures counted honestly (UIR-26 pattern).
  const [bulkBusy, setBulkBusy] = useState<string | null>(null);
  const bulkRun = async (verb: "publish" | "unpublish" | "duplicate") => {
    if (bulkBusy) return;
    const targets = pages.filter((p) => selected.has(p.id));
    if (targets.length === 0) return;
    setBulkBusy(verb);
    let ok = 0;
    for (const p of targets) {
      try {
        if (verb === "duplicate") {
          const r = await duplicateLandingPage(p.id, lpOpts()) as { page?: Record<string, unknown> } | Record<string, unknown>;
          reflectLandingDto((r as { page?: Record<string, unknown> })?.page ?? (r as Record<string, unknown>));
        } else {
          const status = verb === "publish" ? "published" : "private";
          const r = await publishLandingPage(p.id, lpOpts(), { status }) as { page?: Record<string, unknown> } | Record<string, unknown>;
          reflectLandingDto((r as { page?: Record<string, unknown> })?.page ?? (r as Record<string, unknown>));
        }
        ok++;
      } catch (e) { console.warn(`bulk ${verb} failed for ${p.id}:`, e); }
    }
    setBulkBusy(null);
    const failed = targets.length - ok;
    if (failed === 0) {
      toast({ tone: verb === "unpublish" ? "warning" : "success", title: verb === "publish" ? t("landing.pagesIndex.published", "Published") : verb === "unpublish" ? t("landing.pagesIndex.unpublished", "Unpublished") : t("landing.pagesIndex.duplicated", "Duplicated"), subtitle: `${ok} page${ok === 1 ? "" : "s"}` });
      clearSelection();
    } else if (ok === 0) toast({ tone: "danger", title: t("landing.pagesIndex.nothingChanged", "Nothing changed"), subtitle: t("landing.pagesIndex.everyPageFailedRetryInAMoment", "Every page failed. Retry in a moment.") });
    else toast({ tone: "warn", title: t("landing.pagesIndex.partlyApplied", "Partly applied"), subtitle: `${ok} / ${targets.length}` });
  };

  const soon = (title: string, subtitle: string) => toast({ tone: "info", title: i18nT("landing.pagesIndex.titleComingSoon.icu", { value0: title }), subtitle });
  const lpOpts = () => studioRequestOptions("landing-pages");
  const duplicatePage = async (p: LandingPage) => {
    try {
      const r = await duplicateLandingPage(p.id, lpOpts()) as { page?: Record<string, unknown> } | Record<string, unknown>;
      const dto = (r as { page?: Record<string, unknown> })?.page ?? (r as Record<string, unknown>);
      const np = reflectLandingDto(dto);
      toast({ tone: "success", title: t("landing.pagesIndex.pageDuplicated", "Page duplicated"), subtitle: i18nT("landing.pagesIndex.aDraftCopyOfPTitleWasAdded.icu", { value0: p.title }) });
      nav.go("landing.editor", { id: np.id });
    } catch (e) { console.warn("duplicateLandingPage failed:", e); toast({ tone: "danger", title: t("landing.pagesIndex.couldnTDuplicatePage", "Couldn't duplicate page"), subtitle: t("landing.pagesIndex.noCopyWasCreatedRetryInAMoment", "No copy was created. Retry in a moment.") }); }
  };
  const setPublish = async (p: LandingPage, status: "published" | "private") => {
    try {
      const r = await publishLandingPage(p.id, lpOpts(), { status }) as { page?: Record<string, unknown> } | Record<string, unknown>;
      const dto = (r as { page?: Record<string, unknown> })?.page ?? (r as Record<string, unknown>);
      reflectLandingDto(dto);
      toast({ tone: status === "published" ? "success" : "warning", title: status === "published" ? "Page published" : "Page unpublished", subtitle: t("landing.pagesIndex.pTitleIsNowStatusPublishedLivePrivate.icu", { title: p.title, status }) });
    } catch (e) { console.warn("publishLandingPage failed:", e); toast({ tone: "danger", title: t("landing.pagesIndex.couldnTUpdatePage", "Couldn't update page"), subtitle: t("landing.pagesIndex.thePageIsUnchangedRetryInAMoment", "The page is unchanged. Retry in a moment.") }); }
  };
  const removePage = async (p: LandingPage) => {
    try {
      await deleteLandingPage(p.id, lpOpts());
      removeLandingPageLocal(p.id);
      toast({ tone: "warning", title: t("landing.pagesIndex.pageDeleted", "Page deleted"), subtitle: i18nT("landing.pagesIndex.pTitleWasRemoved.icu", { value0: p.title }) });
    } catch (e) { console.warn("deleteLandingPage failed:", e); toast({ tone: "danger", title: t("landing.pagesIndex.couldnTDeletePage", "Couldn't delete page"), subtitle: t("landing.pagesIndex.thePageIsUnchangedRetryInAMoment", "The page is unchanged. Retry in a moment.") }); }
  };
  const exportPerformance = async () => {
    try {
      const r = await exportLandingPagePerformance(lpOpts(), { format: "csv", scope: "filtered" }) as { downloadUrl?: string; filename?: string };
      if (r?.downloadUrl) {
        const a = document.createElement("a");
        a.href = r.downloadUrl; a.download = r.filename || "landing-performance.csv"; a.rel = "noopener"; a.style.display = "none";
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
        toast({ tone: "success", title: t("landing.pagesIndex.performanceExported", "Performance exported"), subtitle: i18nT("landing.pagesIndex.rFilenameCsvDownloaded.icu", { value0: r.filename || "CSV" }) });
      } else { toast({ tone: "info", title: t("landing.pagesIndex.exportReady", "Export ready"), subtitle: t("landing.pagesIndex.noDownloadUrlReturned", "No download URL returned.") }); }
    } catch (e) { console.warn("exportLandingPagePerformance failed:", e); toast({ tone: "danger", title: t("landing.pagesIndex.exportDidnTStart", "Export didn't start"), subtitle: t("landing.pagesIndex.retryInAMoment", "Retry in a moment.") }); }
  };
  const pageActions = (p: LandingPage) => ({
    onView: () => nav.go("event.public", { eventId: p.id }),
    viewLabel: "View public page",
    onEdit: () => nav.go("landing.editor", { id: p.id }),
    onDuplicate: () => duplicatePage(p),
    moreHeader: p.title || "Landing page",
    moreItems: [
      { icon: <Icon.link size={14} />, label: t("landing.pagesIndex.copyPublicUrl", "Copy public URL"), onClick: () => copyUrl(p.slug) },
      { icon: <Icon.qr size={14} />, label: t("landing.pagesIndex.showQrCode", "Show QR code"), sub: "Coming soon", onClick: () => soon("QR code", "A downloadable QR to this page is coming soon.") },
      "divider" as const,
      { icon: <Icon.chart size={14} />, label: t("landing.pagesIndex.viewAnalytics", "View analytics"), sub: "Coming soon", onClick: () => soon("Page analytics", "Per-page visits and conversion analytics are coming soon.") },
      { icon: <Icon.download size={14} />, label: t("landing.pagesIndex.exportPerformance", "Export performance"), onClick: () => void exportPerformance() },
      "divider" as const,
      { icon: <Icon.eye size={14} />, label: p.status === "published" ? "Unpublish" : "Publish", onClick: () => void setPublish(p, p.status === "published" ? "private" : "published") },
      { icon: <Icon.trash size={14} />, label: t("landing.pagesIndex.deletePage", "Delete page"), danger: true, onClick: () => void removePage(p) },
    ],
  });

  const filterChip = (id: string, label: string) => (
    <Chip key={id} selected={filter === id} onClick={() => setFilter(id)}>{label}</Chip>
  );

  return (
    <Screen rail="events" tabs={eventsSectionTabs(nav, "landing-pages", useEventSectionCounts())} activeTab="landing-pages">
      <PageHeader
        title={t("landing.pagesIndex.landingPages", "Landing Pages")}
        subtitle={i18nT("landing.pagesIndex.pagesLengthEventPagesTotalTolocalestringVisitsTotalconvRegistrationsThis.icu", { value0: pages.length, value1: total.toLocaleString(), value2: totalConv })}
        actions={<>
          <Btn variant="secondary" icon={<Icon.download size={14} />} onClick={() => tabExport("landing pages", pages as unknown as Record<string, unknown>[])}>{t("common.actions.export", "Export")}</Btn>
          <Btn icon={<Icon.plus />} onClick={() => setOpenPicker(true)}>{t("landing.pagesIndex.newLandingPage", "New landing page")}</Btn>
        </>}
      />
      <div style={{ padding: "16px 24px 0", display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
        <StatTile label={t("landing.pagesIndex.eventPages", "Event pages")} value={pages.length} icon={<Icon.browser size={14} />} accent={T.p1} />
        <StatTile label={t("landing.pagesIndex.visits30d", "Visits · 30d")} value={total.toLocaleString()} icon={<Icon.chart size={14} />} accent={T.tDarkBlue} />
        <StatTile label={t("landing.pagesIndex.registrations", "Registrations")} value={totalConv} icon={<Icon.users size={14} />} accent={T.green1} />
        <StatTile label={t("common.status.published", "Published")} value={publishedCount} icon={<Icon.check size={14} />} accent={T.orange1} />
      </div>
      <FilterBar>
        <Input placeholder={t("landing.pagesIndex.findAPageByTitleEventOrSlug", "Find a page by title, event, or slug")} value={query} onChange={(e) => setQuery(e.target.value)} full={false} leading={<Icon.search size={14} />} size="sm" style={{ width: 280 }} />
        <span style={{ width: 1, height: 20, background: T.border, margin: "0 4px" }} />
        {filterChip("all", `All · ${pages.length}`)}
        {filterChip("published", `Published · ${publishedCount}`)}
        {filterChip("draft", `Drafts · ${draftCount}`)}
        {filterChip("private", `Private · ${privateCount}`)}
        <span style={{ width: 1, height: 20, background: T.border, margin: "0 4px" }} />
        <FilterDropdown
          label={i18nT("landing.pagesIndex.sortSortLabelsSort.icu", { value0: SORT_LABELS[sort] })}
          active={sort !== "visits"}
          options={Object.values(SORT_LABELS)}
          onPick={(label) => { const key = Object.keys(SORT_LABELS).find((k) => SORT_LABELS[k] === label); if (key) setSort(key); }}
          onClear={() => setSort("visits")}
        />
        <span style={{ marginLeft: "auto" }} />
        <ViewToggle mode={view} onChange={setView} />
      </FilterBar>

      {selected.size > 0 ? (
        <div style={{ margin: "0 24px 12px", padding: "10px 14px", background: T.n1, color: T.white, borderRadius: 8, display: "flex", alignItems: "center", gap: 12, boxShadow: T.shadowMd }}>
          <span style={{ fontSize: 13, fontWeight: 600 }}>{selected.size} {t("landing.pagesIndex.selected", "selected")}</span>
          <span style={{ width: 1, height: 18, background: "rgba(255,255,255,0.18)" }} />
          {/* ui-raw: control on the dark bulk-selection bar — needs Button's on-brand treatment (capability request 3); a neutral Btn is invisible on this fill. */}
          <button disabled={bulkBusy !== null} onClick={() => void bulkRun("publish")} style={bulkBtn}><Icon.eye size={12} /> {t("common.actions.publish", "Publish")}</button>
          {/* ui-raw: control on the dark bulk-selection bar — needs Button's on-brand treatment (capability request 3); a neutral Btn is invisible on this fill. */}
          <button disabled={bulkBusy !== null} onClick={() => void bulkRun("unpublish")} style={bulkBtn}><Icon.lock size={12} /> {t("landing.pagesIndex.unpublish", "Unpublish")}</button>
          {/* ui-raw: control on the dark bulk-selection bar — needs Button's on-brand treatment (capability request 3); a neutral Btn is invisible on this fill. */}
          <button disabled={bulkBusy !== null} onClick={() => void bulkRun("duplicate")} style={bulkBtn}><Icon.copy size={12} /> {t("landing.pagesIndex.duplicate", "Duplicate")}</button>
          {/* ui-raw: control on the dark bulk-selection bar — needs Button's on-brand treatment (capability request 3); a neutral Btn is invisible on this fill. */}
          <button disabled={bulkBusy !== null} onClick={() => void exportPerformance()} style={bulkBtn}><Icon.download size={12} /> {t("common.actions.export", "Export")}</button>
          <span style={{ flex: 1 }} />
          {/* ui-raw: control on the dark bulk-selection bar — needs Button's on-brand treatment (capability request 3); a neutral Btn is invisible on this fill. */}
          <button onClick={clearSelection} style={{ background: "transparent", border: "none", color: "rgba(255,255,255,0.7)", fontSize: 12, fontWeight: 500, cursor: "pointer", fontFamily: T.font, padding: "4px 6px" }}>{t("common.actions.clear", "Clear")}</button>
        </div>
      ) : null}

      {/* Explainer belongs above the list (it was a hand-rolled ExplainerFooter
          copy placed after the content, so it rendered below the no-data card —
          operator report 2026-07-30). */}
      <div style={{ padding: "0 24px" }}>
        <Banner tone="info">
          <b>{t("landing.pagesIndex.eventPages", "Event pages")}</b> {t("landing.pagesIndex.registerGuestsForASingleEventOnePagePerEventWithIts", "register guests for a single event — one page per event, with its own URL, theme, and ticket selector. For a public catalog that lists many events on one URL, use")}{" "}<span onClick={() => nav.go("hub.pages")} style={{ color: T.p1, fontWeight: 600, cursor: "pointer" }}>{t("landing.pagesIndex.eventHubs", "Event hubs")}</span> {t("landing.pagesIndex.instead", "instead.")}</Banner>
      </div>

      <div style={{ flex: 1, overflow: "auto", padding: "0 24px 24px" }}>
        {sortedPages.length === 0 ? (
          <PageState skeleton={view === "grid" ? "cards" : "table"} noun={t("events.sectionState.nounLandingPages", "landing pages")} icon={<Icon.browser size={20} />}
            empty filtered={!!(query || filter !== "all")} onClearFilters={() => { setQuery(""); setFilter("all"); }}
            action={<Btn icon={<Icon.plus size={14} />} onClick={() => setOpenPicker(true)}>{t("landing.pagesIndex.newLandingPage", "New landing page")}</Btn>} />
        ) : view === "grid" ? (
          (
            <div style={{ display: "grid", gridTemplateColumns: CARD_GRID, gap: 16 }}>
              {sortedPages.map((p) => (
                <Card key={p.id} style={{ padding: 0, overflow: "hidden", cursor: "pointer", display: "flex", flexDirection: "column" }} onClick={() => nav.go("landing.editor", { id: p.id })}>
                  <div style={{ height: 96, background: `linear-gradient(135deg, ${p.hue}, ${T.p1})`, position: "relative", display: "flex", alignItems: "flex-end", padding: 12 }}>
                    <div style={{ position: "absolute", top: 10, right: 10 }}>
                      {p.status === "published" ? <Pill tone="success" dot>{t("common.status.published", "Published")}</Pill> : p.status === "private" ? <Pill tone="warn"><Icon.lock size={10} /> {t("landing.pagesIndex.private", "Private")}</Pill> : <Pill>{t("common.status.draft", "Draft")}</Pill>}
                    </div>
                    <span style={{ fontSize: 18, fontWeight: 800, color: T.white }}>{p.title.split(" ").map((w) => w[0]).slice(0, 2).join("")}</span>
                  </div>
                  <div style={{ padding: "12px 14px", display: "flex", flexDirection: "column", gap: 6, flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: T.n1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{p.title}</div>
                    <div style={{ fontFamily: T.fontMono, fontSize: 11, color: T.p1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{publicHost("attend")}/{p.slug}</div>
                    <div style={{ marginTop: "auto", paddingTop: 8, borderTop: `1px solid ${T.border}`, display: "flex", justifyContent: "space-between", fontSize: 12, color: T.soft }}>
                      <span>{p.visits === 0 ? t('landing.pagesIndex.text') : /* TODO(i18n): replace template-literal with t('landing.pagesIndex.text2', { ... }) */`${p.visits.toLocaleString()} visits`}</span>
                      <span>{p.conv === 0 ? "—" : /* TODO(i18n): replace template-literal with t('landing.pagesIndex.text3', { ... }) */`${p.conv} reg · ${p.convPct.toFixed(1)}%`}</span>
                    </div>
                  </div>
                  <CardActionBar {...pageActions(p)} />
                </Card>
              ))}
            </div>
          )
        ) : (
          <Card style={{ padding: 0 }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: T.font, fontSize: 13 }}>
              <thead>
                <tr style={{ background: T.n6, color: T.soft, fontSize: 11, textTransform: "uppercase", fontWeight: 600 }}>
                  <th style={{ ...thStyle, width: 36, paddingRight: 0, boxSizing: "content-box" }}><Checkbox checked={allChecked} indeterminate={someChecked} onChange={toggleAll} /></th>
                  <th style={thStyle}>{t("landing.pagesIndex.page", "Page")}</th>
                  <th style={thStyle}>{t("landing.pagesIndex.event", "Event")}</th>
                  <th style={thStyle}>{t("landing.pagesIndex.url", "URL")}</th>
                  <th style={thStyle}>{t("landing.pagesIndex.status", "Status")}</th>
                  <th style={thStyle}>{t("landing.pagesIndex.visits30d", "Visits · 30d")}</th>
                  <th style={thStyle}>{t("landing.pagesIndex.conv", "Conv.")}</th>
                  <th style={thStyle}>{t("landing.pagesIndex.edited", "Edited")}</th>
                  <th style={{ ...thStyle, width: 150 }}>{t("landing.pagesIndex.actions", "Actions")}</th>
                </tr>
              </thead>
              <tbody>
                {sortedPages.map((p, i) => (
                  <tr key={p.id} onClick={() => nav.go("landing.editor", { id: p.id })} style={{ borderTop: i ? `1px solid ${T.border}` : "none", cursor: "pointer", background: selected.has(p.id) ? T.p4 : T.white, borderLeft: `3px solid ${rowAccent(p.status)}` }}>
                    <td style={{ ...tdStyle, paddingRight: 0 }} onClick={(e) => e.stopPropagation()}><Checkbox checked={selected.has(p.id)} onChange={() => toggle(p.id)} /></td>
                    <td style={tdStyle}>
                      <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                        <div style={{ width: 56, height: 40, borderRadius: 4, background: `linear-gradient(135deg, ${p.hue}, ${T.p1})`, color: T.white, display: "inline-flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 12, flexShrink: 0 }}>
                          {p.title.split(" ").map((w) => w[0]).slice(0, 2).join("")}
                        </div>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ color: T.n1, fontWeight: 600 }}>{p.title}</div>
                          <div style={{ color: T.soft, fontSize: 11 }}>{p.theme}</div>
                        </div>
                      </div>
                    </td>
                    <td style={{ ...tdStyle, color: T.n1 }}>{p.ev}</td>
                    <td style={tdStyle} onClick={(e) => e.stopPropagation()}>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontFamily: T.fontMono, fontSize: 12, color: T.p1 }}>
                        {publicHost("attend")}/{p.slug}
                        <span onClick={() => copyUrl(p.slug)} style={{ cursor: "pointer", display: "inline-flex" }}><Icon.copy size={11} style={{ color: T.soft }} /></span>
                      </span>
                    </td>
                    <td style={tdStyle}>
                      {p.status === "published" ? <Pill tone="success" dot>{t("common.status.published", "Published")}</Pill> : p.status === "private" ? <Pill tone="warn"><Icon.lock size={10} /> {t("landing.pagesIndex.private", "Private")}</Pill> : <Pill>{t("common.status.draft", "Draft")}</Pill>}
                    </td>
                    <td style={{ ...tdStyle, color: T.n1, fontVariantNumeric: "tabular-nums", fontWeight: 600 }}>{p.visits === 0 ? <span style={{ color: T.soft, fontWeight: 400 }}>—</span> : p.visits.toLocaleString()}</td>
                    <td style={tdStyle}>
                      {p.conv === 0 ? <span style={{ color: T.soft }}>—</span> : <>
                        <div style={{ color: T.n1, fontWeight: 600 }}>{p.conv}</div>
                        <div style={{ fontSize: 11, color: p.convPct >= 5 ? T.green1 : T.soft }}>{p.convPct.toFixed(2)}%</div>
                      </>}
                    </td>
                    <td style={{ ...tdStyle, color: T.soft, fontSize: 12 }}>{p.edited}</td>
                    <td style={tdStyle} onClick={(e) => e.stopPropagation()}><RowActions {...pageActions(p)} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )}
      </div>

      {openPicker ? <NewEventPagePicker onClose={() => setOpenPicker(false)} /> : null}
    </Screen>
  );
}

const bulkBtn: React.CSSProperties = {
  display: "inline-flex", alignItems: "center", gap: 6,
  background: "rgba(255,255,255,0.08)", color: T.white,
  border: "1px solid rgba(255,255,255,0.18)", borderRadius: 6,
  padding: "5px 10px", cursor: "pointer", fontFamily: T.font, fontSize: 12, fontWeight: 600,
};

const pickerRow: React.CSSProperties = {
  display: "flex", alignItems: "center", gap: 12, padding: "12px 14px",
  border: `1px solid ${T.border}`, borderRadius: 8, background: T.white,
  cursor: "pointer", textAlign: "left", fontFamily: T.font, width: "100%",
};

/** New event page picker — pick which event this page is for. */
function NewEventPagePicker({ onClose }: { onClose: () => void }) {
  const t = useT();
  const nav = useNav();
  const { events } = useEvents();
  const pages = useLandingPages();
  const hasPage = new Set(pages.map((p) => p.ev));
  const noPageYet = events.filter((e) => !hasPage.has(e.title) && e.status !== "past");
  const go = (ev: UIEvent) => { onClose(); nav.go("landing.editor", { id: ev.id }); };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(51,51,51,0.4)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: 640, maxHeight: "82vh", background: T.white, borderRadius: 12, boxShadow: T.shadowLg, overflow: "hidden", display: "flex", flexDirection: "column" }}>
        <div style={{ padding: "20px 24px", borderBottom: `1px solid ${T.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <h3 style={{ fontSize: 18, fontWeight: 700, color: T.n1, margin: 0 }}>{t("landing.pagesIndex.createANewEventPage", "Create a new event page")}</h3>
            <p style={{ fontSize: 13, color: T.soft, margin: "4px 0 0" }}>{t("landing.pagesIndex.pickTheEventThisPageIsForEachEventPageHasIts", "Pick the event this page is for. Each event page has its own URL and ticket selector.")}</p>
          </div>
          <IconBtn label={t("common.actions.close", "Close")} onClick={onClose}><Icon.x /></IconBtn>
        </div>
        <div style={{ overflow: "auto", padding: "16px 24px 20px" }}>
          {noPageYet.length > 0 ? (
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: T.soft, textTransform: "uppercase", letterSpacing: 0.5, margin: "4px 0 10px" }}>{t("landing.pagesIndex.eventsThatNeedAPage", "Events that need a page ·")}{noPageYet.length}</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {noPageYet.map((ev) => (
                  <button key={ev.id} onClick={() => go(ev)} style={pickerRow}>
                    <span style={{ width: 40, height: 40, borderRadius: 6, background: `linear-gradient(135deg, ${ev.hue || T.p1}, ${T.p1})`, color: T.white, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontWeight: 700, fontSize: 13 }}>
                      {ev.title.split(" ").map((w) => w[0]).slice(0, 2).join("")}
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: T.n1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{ev.title}</div>
                      <div style={{ fontSize: 12, color: T.soft, marginTop: 2 }}>{ev.date}</div>
                    </div>
                    <Pill tone="info">{t("landing.pagesIndex.noPageYet", "No page yet")}</Pill>
                    <Icon.arrow size={13} style={{ color: T.soft }} />
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div style={{ padding: "30px 0", textAlign: "center", color: T.soft, fontSize: 13 }}>{t("landing.pagesIndex.everyEventAlreadyHasAPageStartANewEventBelow", "Every event already has a page. Start a new event below.")}</div>
          )}
        </div>
        <div style={{ padding: "14px 24px", borderTop: `1px solid ${T.border}`, background: T.n6, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontSize: 12, color: T.soft }}>{t("landing.pagesIndex.lookingToListManyEventsOnOneUrlUse", "Looking to list many events on one URL? Use")}{" "}<button onClick={() => { onClose(); nav.go("hub.pages"); }} style={{ background: "transparent", border: "none", color: T.p1, fontWeight: 600, cursor: "pointer", fontFamily: T.font, fontSize: 12 }}>{t("landing.pagesIndex.eventHubs", "Event hubs")}</button> {t("landing.pagesIndex.instead", "instead.")}</span>
          <Btn size="sm" icon={<Icon.plus size={12} />} onClick={() => { onClose(); nav.go("newjourneys"); }}>{t("landing.pagesIndex.startANewEvent", "Start a new event")}</Btn>
        </div>
      </div>
    </div>
  );
}
